/* eslint prefer-template: "off" */

/* eslint no-console: "off" */
import { Component } from 'react';

import kurentoUtils from 'kurento-utils';
import PropTypes from 'prop-types';
import io from 'socket.io-client';

import withToken from './BMXAuthenticationContainer';

import BMXCameraComponent from '../components/BMXCameraComponent';

import {
  STATE_CONNECTED,
  STATE_IDLE,
  STATE_STOPPED,
  STATE_VIEWER,
  STATE_WAIT_PRESENTER_CONNECTED,
  STATE_WAIT_PRESENTER_UNAVAILABLE,
} from '../constants';

import env from '../../../../env';
import { BMX_LOGGER } from '../../../staticData';

const logger = (text) => {
  BMX_LOGGER && console.log(text);
};

const onError = (error) => {
  logger('onError called, error: ' + JSON.stringify(error));
};

class BMXCameraContainer extends Component {
  static getDerivedStateFromProps(props, prevState) {
    if (
      prevState.channel &&
      props.camera &&
      props.camera.liveViewId &&
      prevState.channel !== props.camera.liveViewId
    ) {
      return { channel: props.camera.liveViewId };
    }
    return null;
  }

  constructor(props) {
    super(props);
    this.state = {
      connectionState: -1,
      token: props.token,
      channel: props.camera.liveViewId,
      userMessage: null,
    };

    this.socket = null;
    this.videoOutput = null;
    this.webRtcPeer = null;
    this.reloadTimeOut = null;
    this.setConnectionState = this.setConnectionState.bind(this);
    this.notify = this.notify.bind(this);
    this.sendMessage = this.sendMessage.bind(this);
    this.dispose = this.dispose.bind(this);
    this.reloadPage = this.reloadPage.bind(this);
    this.viewerResponse = this.viewerResponse.bind(this);
    this.connectResponse = this.connectResponse.bind(this);
    this.stop = this.stop.bind(this);
    this.viewer = this.viewer.bind(this);
    this.showSpinner = this.showSpinner.bind(this);
    this.hideSpinner = this.hideSpinner.bind(this);
    this.onIceCandidate = this.onIceCandidate.bind(this);
    this.onOfferViewer = this.onOfferViewer.bind(this);
    this.showUserMessage = this.showUserMessage.bind(this);
    this.stopCommunication = this.stopCommunication.bind(this);
    this.startCommunication = this.startCommunication.bind(this);
  }

  componentDidMount() {
    this.startCommunication();
  }

  componentDidUpdate(prevProps, prevState) {
    const { channel } = this.state;
    if (prevState.channel !== channel) {
      this.stopCommunication();
      this.startCommunication();
    }
  }

  componentWillUnmount() {
    this.stopCommunication();
  }

  onIceCandidate(candidate) {
    const { channel } = this.state;
    logger('Local ICE candidate: ' + JSON.stringify(candidate));
    const message = {
      id: 'onIceCandidate',
      channel,
      candidate,
    };
    this.sendMessage(message);
  }

  onOfferViewer(error, offerSdp) {
    if (error) {
      return onError(error);
    }
    const { channel } = this.state;
    const message = {
      id: 'viewer',
      channel,
      sdpOffer: offerSdp,
    };
    return this.sendMessage(message);
  }

  setConnectionState(state, message) {
    const { connectionState: previousState } = this.state;
    if (previousState === state) return;

    let currentState;
    if (state === STATE_IDLE) {
      this.notify();
      currentState = state;
    } else if (state === STATE_VIEWER) {
      currentState = state;
    } else if (state === STATE_CONNECTED) {
      currentState = state;
    } else if (state === STATE_WAIT_PRESENTER_CONNECTED) {
      this.dispose();
      currentState = state;
    } else if (state === STATE_WAIT_PRESENTER_UNAVAILABLE) {
      this.showUserMessage(message || 'Camera is not available');
      this.dispose();
      currentState = state;
    } else if (state === STATE_STOPPED) {
      this.dispose();
      currentState = state;
      this.reloadTimeOut = setTimeout(() => {
        if (currentState === STATE_STOPPED) {
          this.setConnectionState(STATE_IDLE);
        }
      }, 3000);
    } else {
      logger('Invalid state' + state);
    }
    this.setState({ connectionState: currentState });
  }

  sendCameraStatus = (status) => {
    const { sendBMXCameraStatus, buildingId, camera } = this.props;
    sendBMXCameraStatus(buildingId, camera?.id, { status });
  };

  async startCommunication() {
    const { token, channel } = this.state;
    const { BMX_URL } = env;
    if (!token || !channel) {
      return this.showUserMessage('Cannot Authenticate');
    }

    this.socket = await io(BMX_URL, {
      autoConnect: true,
      transports: ['websocket'],
      secure: true,
      reconnection: false,
      query: 'auth_token=' + token,
    });

    this.videoOutput = document.getElementById(channel);
    this.setConnectionState(STATE_IDLE);
    this.showSpinner();

    this.socket.on('error', (err) => {
      logger('Received error: ' + err);
      this.reloadPage();
    });

    return this.socket.on('connect', () => {
      this.socket.on('disconnect', (reason) => {
        logger('socket.io disconnected: ' + reason);
        this.reloadPage();
      });

      this.socket.on('message', (message) => {
        logger('Received message: ' + message);
        const parsedMessage = JSON.parse(message);

        const messageId = parsedMessage.id;
        if (messageId === 'viewerResponse') {
          this.viewerResponse(parsedMessage);
        } else if (messageId === 'stopCommunication') {
          this.setConnectionState(STATE_WAIT_PRESENTER_UNAVAILABLE);
        } else if (messageId === 'iceCandidate') {
          this.webRtcPeer.addIceCandidate(parsedMessage.candidate);
        } else if (messageId === 'connectResponse') {
          this.connectResponse(parsedMessage);
        } else if (messageId === 'event') {
          const eventName = parsedMessage.name;
          if (eventName === 'presenterUnavailable') {
            this.sendCameraStatus('unavailable');
            this.setConnectionState(STATE_WAIT_PRESENTER_UNAVAILABLE);
          } else if (eventName === 'presenterConnected') {
            const { connectionState } = this.state;
            if (!(connectionState === STATE_CONNECTED)) {
              this.setConnectionState(STATE_WAIT_PRESENTER_CONNECTED);
            }
          } else if (eventName === 'presenterReady') {
            this.sendCameraStatus('available');
            // Try to connect
            this.viewer();
          } else {
            logger('Unrecognized event: ' + message);
          }
        } else if (messageId === 'error') {
          // Internal Error
          logger('Received an internal error: ' + parsedMessage.reason);
          this.reloadPage();
        } else {
          logger('Unrecognized message: ' + parsedMessage);
        }
      });
    });
  }

  stopCommunication() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    if (this.videoOutput) {
      this.videoOutput = null;
    }
    if (this.webRtcPeer) {
      this.webRtcPeer.dispose();
      this.webRtcPeer = null;
    }
    if (this.reloadTimeOut) {
      clearTimeout(this.reloadTimeOut);
    }
  }

  sendMessage(message) {
    const jsonMessage = JSON.stringify(message);
    logger('Sending message: ' + jsonMessage);
    this.socket && this.socket.send(jsonMessage);
  }

  showSpinner() {
    logger('show spinner');
    this.showUserMessage('Loading...');
    if (this.videoOutput) {
      this.videoOutput.addEventListener('playing', () => {
        this.showUserMessage('');
      });
    }
  }

  hideSpinner() {
    logger('hide spinner');
    this.showUserMessage('');
  }

  showUserMessage(text) {
    this.setState({ userMessage: text });
  }

  notify() {
    const { channel } = this.state;
    logger('Asking the server to send the current state');
    const message = {
      id: 'notify',
      channel,
    };
    this.sendMessage(message);
  }

  dispose() {
    const { userMessage } = this.state;
    if (this.webRtcPeer) {
      this.webRtcPeer.dispose();
      this.webRtcPeer = null;
    }
    if (userMessage === 'Loading...') {
      this.hideSpinner(this.videoOutput);
    }
  }

  stop() {
    const { channel } = this.state;
    if (this.webRtcPeer) {
      const message = {
        id: 'stop',
        channel,
      };
      this.sendMessage(message);
      this.dispose();
    }
    return this.setConnectionState(STATE_STOPPED);
  }

  viewer() {
    if (!this.webRtcPeer) {
      this.showSpinner(this.videoOutput);
      const options = {
        remoteVideo: this.videoOutput,
        onicecandidate: this.onIceCandidate,
      };
      this.webRtcPeer = kurentoUtils.WebRtcPeer.WebRtcPeerRecvonly(
        options,
        (error) => {
          if (error) {
            return onError(error);
          }
          return this.webRtcPeer.generateOffer(this.onOfferViewer);
        },
      );
    }
  }

  reloadPage() {
    logger('reload page');
    this.showSpinner();
    this.stop();
  }

  viewerResponse(message) {
    if (message.response === 'accepted') {
      this.webRtcPeer.processAnswer(message.sdpAnswer);
      this.setConnectionState(STATE_VIEWER);
    } else {
      const errMessage = message.message ? message.message : 'Unknown error';
      logger('Call not accepted: ' + errMessage);
      this.setConnectionState(
        STATE_WAIT_PRESENTER_UNAVAILABLE,
        'Call not accepted: ' + errMessage,
      );
    }
  }

  connectResponse(message) {
    if (message.response === 'accepted') {
      this.setConnectionState(STATE_CONNECTED);
    } else {
      const errMessage = message.message ? message.message : 'Unknown error';
      logger('Connect not accepted: ' + errMessage);
      this.stop();
    }
  }

  render() {
    const { channel: id, userMessage, connectionState } = this.state;
    const { buildingId, buildingName, cameraName, showCameraTitle } =
      this.props;
    const shouldShowReload =
      connectionState === (STATE_WAIT_PRESENTER_UNAVAILABLE || STATE_STOPPED);
    const props = {
      showCameraTitle,
      buildingId,
      buildingName,
      cameraName,
      id,
      userMessage,
      shouldShowReload,
      reloadPage: this.reloadPage,
    };
    return <BMXCameraComponent {...props} />;
  }
}

BMXCameraContainer.defaultProps = {
  showCameraTitle: true,
};

BMXCameraContainer.propTypes = {
  token: PropTypes.string.isRequired,
  camera: PropTypes.instanceOf(Object).isRequired,
  buildingId: PropTypes.number.isRequired,
  buildingName: PropTypes.string.isRequired,
  cameraName: PropTypes.string.isRequired,
  showCameraTitle: PropTypes.bool,
  sendBMXCameraStatus: PropTypes.func.isRequired,
};

export default withToken(BMXCameraContainer);
